import { useState, useCallback, useMemo } from "react";

export interface UseEditableFieldOptions {
  value: string;
  onSave: (value: string) => Promise<string | void>;
  formatDisplay?: (value: string) => string;
  parseInput?: (value: string) => string;
  validate?: (value: string) => boolean;
}

export default function useEditableField({
  value,
  onSave,
  formatDisplay,
  parseInput,
  validate,
}: UseEditableFieldOptions) {
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [lastPropValue, setLastPropValue] = useState(value);
  const [hasError, setHasError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  if (value !== lastPropValue) {
    setLastPropValue(value);
    if (!isFocused) setEditValue(value);
  }

  const isValid = !validate || validate(editValue);

  const normalizeValue = useCallback(
    (input: string) => (parseInput ? parseInput(input) : input),
    [parseInput]
  );

  const save = useCallback(async () => {
    if (validate && !validate(editValue)) return;
    const finalValue = normalizeValue(editValue);
    const currentValue = normalizeValue(value);
    if (finalValue === currentValue) return;
    setIsSaving(true);
    try {
      const savedValue = await onSave(finalValue);
      if (typeof savedValue === "string") setEditValue(savedValue);
    } finally {
      setIsSaving(false);
    }
  }, [editValue, value, onSave, normalizeValue, validate]);

  const displayValue = useMemo(() => {
    if (isFocused) return editValue;
    if (hasError) return editValue;
    const raw = editValue.trim() === "" ? value : editValue;
    if (!formatDisplay || raw.trim() === "") return raw;
    return formatDisplay(raw);
  }, [formatDisplay, editValue, value, isFocused, hasError]);

  const onChange = useCallback(
    (nextValue: string) => {
      if (hasError && (!validate || validate(nextValue))) setHasError(false);
      setEditValue(nextValue);
    },
    [hasError, validate]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (isValid) {
          setHasError(false);
          save();
          (e.target as HTMLElement).blur();
        } else {
          setHasError(true);
        }
      } else if (e.key === "Escape") {
        setEditValue(value);
        setHasError(false);
        (e.target as HTMLElement).blur();
      }
    },
    [save, value, isValid]
  );

  const onBlur = useCallback(() => {
    setIsFocused(false);
    if (isValid) {
      setHasError(false);
      save();
    } else {
      setHasError(true);
    }
  }, [save, isValid]);

  const onFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    e.target.select();
  }, []);

  return {
    displayValue,
    onChange,
    onKeyDown,
    onBlur,
    onFocus,
    isSaving,
    hasError: hasError && !isValid,
  };
}
